if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.pagination = this.querySelector('[data-pdp-media-pagination]');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.initProductMediaCarousel();
        this.initProductMediaPagination();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        if (currentElement) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          currentElement.classList.add('is-active');
        }

        this.updateProductMediaControls(currentElement);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      initProductMediaCarousel() {
        if (!this.hasAttribute('data-product-media-carousel')) return;

        this.carouselSlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!this.carouselSlider) return;

        this.carouselPrevButton = this.querySelector('.product-media-slider-buttons .carousel-prev');
        this.carouselNextButton = this.querySelector('.product-media-slider-buttons .carousel-next');

        const initReusableCarousel = () => {
          if (!window.ReusableCarousel) return;

          new window.ReusableCarousel({
            selector: `#${this.id}`,
            container: '[id^="Slider-Gallery"]',
            track: '[id^="Slider-Gallery"]',
            slide: '.pdp-media-carousel > [id^="Slide-"]',
            prev: '.carousel-prev',
            next: '.carousel-next',
            showDots: false,
            scrollCount: 1,
            loop: true,
            enableDrag: true,
          });
        };

        if (window.ReusableCarousel) {
          initReusableCarousel();
        } else {
          window.addEventListener('load', initReusableCarousel, { once: true });
        }

        this.carouselSlider.addEventListener('scroll', debounce(this.syncProductMediaCarousel.bind(this), 100), {
          passive: true,
        });

        window.addEventListener('resize', debounce(this.updateProductMediaControls.bind(this, null), 200), {
          passive: true,
        });

        this.updateProductMediaControls();
      }

      initProductMediaPagination() {
        if (!this.pagination || !this.carouselSlider) return;

        this.paginationDots = Array.from(this.pagination.querySelectorAll('[data-pdp-media-dot]'));
        this.paginationDotLabel = this.pagination.dataset.pdpMediaDotLabel || '';

        this.paginationDots.forEach((dot, dotIndex) => {
          dot.addEventListener('click', () => {
            this.scrollToProductMediaIndex(this.getSlideIndexForDot(dotIndex));
          });
        });

        this.updateProductMediaControls();
      }

      getProductMediaSlides() {
        if (!this.carouselSlider) return [];

        // Only the direct slide children — nested nodes (media toggle button, deferred-media)
        // also carry a data-media-id and would otherwise double/triple the slide count.
        const slides = Array.from(this.carouselSlider.querySelectorAll(':scope > [data-media-id]'));
        const visibleSlides = slides.filter((slide) => slide.offsetParent !== null);

        return visibleSlides.length ? visibleSlides : slides;
      }

      getProductMediaDotCount(slideCount) {
        if (!this.paginationDots || !this.paginationDots.length) return 0;

        return Math.min(slideCount, this.paginationDots.length);
      }

      getSlideIndexForDot(dotIndex) {
        const slides = this.getProductMediaSlides();
        const dotCount = this.getProductMediaDotCount(slides.length);
        if (!dotCount) return 0;

        // Dots repeat every `dotCount` slides, so a dot targets the slide at that
        // position within the page the gallery is currently on.
        const pageStart = Math.floor(this.getCurrentProductMediaIndex() / dotCount) * dotCount;

        return Math.min(pageStart + dotIndex, slides.length - 1);
      }

      getCurrentProductMediaIndex(currentElement = null) {
        const slides = this.getProductMediaSlides();
        if (!slides.length) return 0;

        if (currentElement) {
          const index = slides.indexOf(currentElement);
          if (index >= 0) return index;
        }

        return slides.reduce((closestIndex, slide, index) => {
          const closestSlide = slides[closestIndex];
          const currentDistance = Math.abs(slide.offsetLeft - this.carouselSlider.scrollLeft);
          const closestDistance = Math.abs(closestSlide.offsetLeft - this.carouselSlider.scrollLeft);

          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);
      }

      updateProductMediaControls(currentElement = null) {
        if (!this.carouselSlider) return;

        const slides = this.getProductMediaSlides();
        if (!slides.length) return;

        const currentIndex = this.getCurrentProductMediaIndex(currentElement);

        this.updateProductMediaArrows(currentIndex, slides.length);
        this.updateProductMediaPagination(currentIndex, slides);
      }

      updateProductMediaArrows(currentIndex, slideCount) {
        if (!this.carouselPrevButton && !this.carouselNextButton) return;

        // Scroll position is checked as well as the index so the arrows also settle
        // correctly when more than one slide is visible at a time.
        const scrollLeft = Math.abs(this.carouselSlider.scrollLeft);
        const maxScroll = this.carouselSlider.scrollWidth - this.carouselSlider.clientWidth;
        const isFirst = currentIndex <= 0 || scrollLeft <= 2;
        const isLast = currentIndex >= slideCount - 1 || maxScroll <= 2 || scrollLeft >= maxScroll - 2;

        if (this.carouselPrevButton) {
          this.carouselPrevButton.disabled = isFirst;
          this.carouselPrevButton.setAttribute('aria-disabled', isFirst ? 'true' : 'false');
        }

        if (this.carouselNextButton) {
          this.carouselNextButton.disabled = isLast;
          this.carouselNextButton.setAttribute('aria-disabled', isLast ? 'true' : 'false');
        }
      }

      updateProductMediaPagination(currentIndex, slides) {
        if (!this.pagination) return;

        const dotCount = this.getProductMediaDotCount(slides.length);
        if (!dotCount) return;

        const activeIndex = currentIndex % dotCount;
        const pageStart = Math.floor(currentIndex / dotCount) * dotCount;

        this.paginationDots.forEach((dot, index) => {
          if (index >= dotCount) {
            dot.hidden = true;
            dot.classList.remove('active');
            dot.setAttribute('aria-current', 'false');
            return;
          }

          dot.hidden = false;

          const isActive = index === activeIndex;
          dot.classList.toggle('active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');

          if (this.paginationDotLabel) {
            const targetIndex = Math.min(pageStart + index, slides.length - 1);
            dot.setAttribute(
              'aria-label',
              this.paginationDotLabel.replace('[index]', targetIndex + 1).replace('[total]', slides.length)
            );
          }
        });
      }

      scrollToProductMediaIndex(index) {
        const slides = this.getProductMediaSlides();
        if (!slides.length) return;

        const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
        const targetSlide = slides[targetIndex];
        if (!targetSlide || !targetSlide.parentElement) return;

        targetSlide.parentElement.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth',
        });
      }

      syncProductMediaCarousel() {
        if (!this.carouselSlider) return;

        const slides = this.getProductMediaSlides();
        if (!slides.length) return;

        const currentElement = slides.reduce((closestSlide, slide) => {
          const currentDistance = Math.abs(slide.offsetLeft - this.carouselSlider.scrollLeft);
          const closestDistance = Math.abs(closestSlide.offsetLeft - this.carouselSlider.scrollLeft);

          return currentDistance < closestDistance ? slide : closestSlide;
        }, slides[0]);

        this.onSlideChanged({ detail: { currentElement } });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateProductMediaControls(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.dataset.axis === 'vertical' && this.mql.matches) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
          return;
        }

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
